import { Button, Flex, pxToRem, useStyles } from '@archipro-design/aria';
import type { ProjectPageLoader } from '@modules/project/type';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useProjectBookMeeting } from '~/modules/project/hook/use-project-book-meeting';
import * as S from './Contacts.mobile.style';

interface ProjectMobileCTAButtonsProps {
    project: ProjectPageLoader['project'];
    showEnquiryCTA?: boolean;
    showSpecifierLoginPrompt?: boolean;
    showWebsiteCTA?: boolean;
}

/**
 * Reusable mobile CTA stack for project details pages.
 */
const ProjectMobileCTAButtons = ({
    project: {
        ID,
        Title,
        Professional,
        categoryPath,
        ClientDirectorCalendarSession,
    },
    showEnquiryCTA = true,
    showSpecifierLoginPrompt = false,
    showWebsiteCTA = false,
}: ProjectMobileCTAButtonsProps) => {
    const { css } = useStyles();
    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(Professional.Website, {
            libraryItemID: ID,
            professionalID: Professional.ID,
            professionalTitle: Professional.Title,
        });

    const { openEnquiryModal } = useEnquiryModal({
        professional: Professional,
        itemType: 'project',
        siteTreeID: ID,
        relatedItem: {
            id: ID,
            type: 'project',
            title: Title,
            category: categoryPath,
        },
    });

    const enquiryLabel = 'Enquire Now';

    const { canBookDirect, openBookingModal, bookingModal } =
        useProjectBookMeeting(ClientDirectorCalendarSession);

    return (
        <>
            <div className={css(S.EnquireContainer)}>
                {showSpecifierLoginPrompt ? (
                    <SpecifierLoginPrompt
                        professionalName={Professional.Title}
                        itemDescription="this project"
                        showBottomBorder={false}
                        className="py-0"
                    />
                ) : (
                    <Flex>
                        {showEnquiryCTA && (
                            <Button
                                size={16}
                                color="dark"
                                className={css(S.EnquireButton)}
                                onClick={() =>
                                    openEnquiryModal({
                                        title: enquiryLabel,
                                        ctaLabel: enquiryLabel,
                                    })
                                }
                            >
                                {enquiryLabel}
                            </Button>
                        )}
                    </Flex>
                )}
            </div>
            {canBookDirect && (
                <div className={css(S.EnquireContainer)}>
                    <Flex>
                        <Button
                            size={16}
                            color="dark"
                            className={css(S.EnquireButton)}
                            onClick={openBookingModal}
                        >
                            Book a Meeting
                        </Button>
                    </Flex>
                </div>
            )}
            {bookingModal}
            {showWebsiteCTA && referralTrackingURL && (
                <Flex
                    gap="space-20"
                    className={css(S.SaveShareContainer)}
                    hAlign="center"
                    vAlign="center"
                >
                    <Button
                        className={css(S.ProfileLink)}
                        as="a"
                        href={referralTrackingURL}
                        size={16}
                        color="dark"
                        transparent
                        outlined
                        variables={{
                            buttonSize20LineHeight: '130%',
                            buttonPadding: pxToRem(15),
                            buttonMinWidth: 'fit-content',
                        }}
                        {...referralTrackingParams}
                        target="_blank"
                        rel="noopener"
                    >
                        VISIT WEBSITE
                    </Button>
                </Flex>
            )}
        </>
    );
};

export default ProjectMobileCTAButtons;
